import { useEffect, useState } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useAction, useMutation } from "convex/react"
import { api } from "../../convex/_generated/api"
import { navigateSafeReturn } from "@/lib/auth-client"
import { errorMessage } from "@/lib/errors"
import { useOnboardingGate } from "@/hooks/use-onboarding-gate"
import { useResumeUpload } from "@/hooks/use-resume-upload"
import type { ExperienceEntry, EducationEntry, ExperienceLevel, WorkMode } from "@/lib/candidate"
import { needsCandidateOnboarding } from "@/lib/candidate"
import { AboutYouFields } from "@/components/candidate/about-you-fields"
import { SkillsField } from "@/components/candidate/skills-field"
import { LinksFields } from "@/components/candidate/links-fields"
import { PreferencesFields } from "@/components/candidate/preferences-fields"
import { ExperienceEditor } from "@/components/experience-editor"
import { EducationEditor } from "@/components/education-editor"
import { StepProgress } from "@/components/onboarding/step-progress"
import { FormTextarea } from "@/components/form/form-field"
import { Logo } from "@/components/logo"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

export const Route = createFileRoute("/onboarding/candidate")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: CandidateOnboarding,
})

type Screen = "resume" | "review"

const REVIEW_STEPS = [
  {
    title: "About you",
    description: "A headline and location help companies find you faster.",
  },
  {
    title: "Skills & links",
    description: "Add what you're great at — skip anything you'd rather add later.",
  },
  {
    title: "Experience & preferences",
    description: "Work history and job preferences round out your profile.",
  },
] as const

type ReviewDraft = {
  headline: string
  location: string
  phone: string
  bio: string
  skills: string
  githubUrl: string
  linkedinUrl: string
  portfolioUrl: string
  experienceLevel: ExperienceLevel | ""
  workMode: WorkMode | ""
  noticePeriod: string
  expectedSalary: string
  experience: ExperienceEntry[]
  education: EducationEntry[]
}

const EMPTY_DRAFT: ReviewDraft = {
  headline: "",
  location: "",
  phone: "",
  bio: "",
  skills: "",
  githubUrl: "",
  linkedinUrl: "",
  portfolioUrl: "",
  experienceLevel: "",
  workMode: "",
  noticePeriod: "",
  expectedSalary: "",
  experience: [],
  education: [],
}

function CandidateOnboarding() {
  const { returnTo: returnToSearch } = Route.useSearch()
  const gate = useOnboardingGate({ role: "seeker", returnTo: returnToSearch })
  const profile = gate.status === "ready" ? gate.profile : undefined
  const update = useMutation(api.profiles.updateProfile)
  const extractProfileFromResume = useAction(api.resumeParseActions.extractProfileFromResume)
  const navigate = useNavigate()

  const [screen, setScreen] = useState<Screen>("resume")
  const [reviewStep, setReviewStep] = useState(0)
  const [reading, setReading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [fromResume, setFromResume] = useState(false)
  const [discoveryConsent, setDiscoveryConsent] = useState(false)
  const [draft, setDraft] = useState<ReviewDraft>(EMPTY_DRAFT)

  const { fileInputRef, uploading, uploadResume } = useResumeUpload((ref) => {
    void runParse(ref.storageId)
  })

  const draftFromProfile = (p: NonNullable<typeof profile>): ReviewDraft => ({
    headline: p.headline ?? "",
    location: p.location ?? "",
    phone: p.phone ?? "",
    bio: p.bio ?? "",
    skills: p.skills?.join(", ") ?? "",
    githubUrl: p.githubUrl ?? "",
    linkedinUrl: p.linkedinUrl ?? "",
    portfolioUrl: p.portfolioUrl ?? "",
    experienceLevel: p.experienceLevel ?? "",
    workMode: p.workMode ?? "",
    noticePeriod: p.noticePeriod ?? "",
    expectedSalary: p.expectedSalary ?? "",
    experience: p.experience ?? [],
    education: p.education ?? [],
  })

  const runParse = async (storageId: NonNullable<typeof profile>["resumeStorageId"]) => {
    if (!storageId) return
    setReading(true)
    const base = profile ? draftFromProfile(profile) : EMPTY_DRAFT
    try {
      const parsed = await extractProfileFromResume({ storageId })
      setDraft({
        ...base,
        headline: parsed.headline ?? base.headline,
        location: parsed.location ?? base.location,
        phone: parsed.phone ?? base.phone,
        skills: parsed.skills?.join(", ") ?? base.skills,
        githubUrl: parsed.githubUrl ?? base.githubUrl,
        linkedinUrl: parsed.linkedinUrl ?? base.linkedinUrl,
        portfolioUrl: parsed.portfolioUrl ?? base.portfolioUrl,
        experienceLevel: (parsed.experienceLevel as ExperienceLevel) ?? base.experienceLevel,
        workMode: (parsed.workMode as WorkMode) ?? base.workMode,
        noticePeriod: parsed.noticePeriod ?? base.noticePeriod,
        expectedSalary: parsed.expectedSalary ?? base.expectedSalary,
        experience: parsed.experience ?? base.experience,
        education: parsed.education ?? base.education,
      })
      setFromResume(true)
      setReviewStep(0)
      setScreen("review")
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't read that resume"))
      setDraft(base)
      setFromResume(false)
      setReviewStep(0)
      setScreen("review")
    } finally {
      setReading(false)
    }
  }

  useEffect(() => {
    if (gate.status !== "ready" || gate.profile.role !== "seeker") return
    if (!needsCandidateOnboarding(gate.profile)) {
      navigateSafeReturn(navigate, returnToSearch, "/candidate")
    }
  }, [gate, navigate, returnToSearch])

  if (gate.status !== "ready" || gate.profile.role === "employer") {
    return (
      <div className="mkt-auth home-wf">
        <Skeleton className="h-40 w-full max-w-lg" />
      </div>
    )
  }

  const finishOnboarding = async () => {
    try {
      await update({ candidateOnboardingAt: Date.now(), candidateVisibility: "private" })
    } catch {
      // Non-fatal — still send them to the desk even if the flag didn't stick.
    }
    navigateSafeReturn(navigate, returnToSearch, "/candidate")
  }

  const saveAndContinue = async () => {
    setSaving(true)
    try {
      await update({
        headline: draft.headline,
        location: draft.location,
        phone: draft.phone,
        bio: draft.bio,
        skills: draft.skills.split(",").map((s) => s.trim()).filter(Boolean),
        githubUrl: draft.githubUrl,
        linkedinUrl: draft.linkedinUrl,
        portfolioUrl: draft.portfolioUrl,
        experienceLevel: draft.experienceLevel,
        workMode: draft.workMode,
        noticePeriod: draft.noticePeriod,
        expectedSalary: draft.expectedSalary,
        experience: draft.experience,
        education: draft.education,
        candidateOnboardingAt: Date.now(),
        candidateVisibility: discoveryConsent ? "discoverable" : "private",
      })
      navigateSafeReturn(navigate, returnToSearch, "/candidate")
    } catch (e) {
      toast.error(errorMessage(e, "Failed to save"))
      setSaving(false)
    }
  }

  const busy = uploading || reading
  const currentStep = REVIEW_STEPS[reviewStep]
  const isLastStep = reviewStep === REVIEW_STEPS.length - 1

  return (
    <div className="mkt-auth home-wf">
      <Link to="/" className="home-wf-brand" aria-label="Hire home">
        <Logo />
      </Link>

      <div className="mkt-auth-card mkt-auth-card--wide">
        <div className="mkt-auth-panel">
          {screen === "resume" ? (
            <>
              <header className="mkt-auth-head">
                <h1 className="mkt-auth-title">Let's get your profile ready</h1>
                <p className="mkt-auth-desc">
                  Upload your resume and we'll fill in the details — you'll check
                  everything before it's saved.
                </p>
                <p className="mkt-auth-proof">
                  <span>PDF or DOCX</span>
                  <span>AI-assisted</span>
                  <span>Edit before save</span>
                </p>
              </header>

              <div className="mkt-auth-actions">
                {gate.profile.resumeFileUrl ? (
                  <>
                    <div className="flex flex-wrap items-center gap-3 rounded-[0.75rem] border border-[rgb(12_15_20/0.12)] p-3 text-sm text-[color:var(--wf-ink)]">
                      <a
                        href={gate.profile.resumeFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="font-bold text-[color:var(--wf-lime-text)] underline-offset-4 hover:underline"
                      >
                        View uploaded resume
                      </a>
                      <button
                        type="button"
                        className="home-wf-btn home-wf-btn--outline !px-3 !py-1.5 !text-sm"
                        disabled={busy}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Replace
                      </button>
                    </div>
                    <button
                      type="button"
                      className="home-wf-btn home-wf-btn--lime w-full"
                      disabled={busy}
                      onClick={() => runParse(gate.profile.resumeStorageId)}
                    >
                      {reading ? "Reading your resume…" : "Use this resume"}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--lime w-full"
                    disabled={busy}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {busy
                      ? reading
                        ? "Reading your resume…"
                        : "Uploading…"
                      : "Upload resume (PDF or DOCX)"}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) void uploadResume(file)
                  }}
                />
                <button
                  type="button"
                  className="mkt-row-action mx-auto"
                  disabled={busy}
                  onClick={() => void finishOnboarding()}
                >
                  Skip for now
                </button>
              </div>
            </>
          ) : (
            <>
              <StepProgress step={reviewStep} total={REVIEW_STEPS.length} />
              <header className="mkt-auth-head !text-left">
                <h1 className="mkt-auth-title">
                  {fromResume && reviewStep === 0
                    ? "Here's what we found"
                    : currentStep.title}
                </h1>
                <p className="mkt-auth-desc">
                  {fromResume && reviewStep === 0
                    ? "Check it over — everything's editable before it's saved."
                    : currentStep.description}
                </p>
              </header>

              <div className="mkt-form space-y-5">
                {reviewStep === 0 && (
                  <AboutYouFields
                    headline={draft.headline}
                    onHeadlineChange={(v) => setDraft((d) => ({ ...d, headline: v }))}
                    location={draft.location}
                    onLocationChange={(v) => setDraft((d) => ({ ...d, location: v }))}
                    experienceLevel={draft.experienceLevel}
                    onExperienceLevelChange={(v) =>
                      setDraft((d) => ({ ...d, experienceLevel: v }))
                    }
                    phone={draft.phone}
                    onPhoneChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
                  />
                )}

                {reviewStep === 1 && (
                  <>
                    <SkillsField
                      value={draft.skills}
                      onChange={(v) => setDraft((d) => ({ ...d, skills: v }))}
                    />
                    <LinksFields
                      githubUrl={draft.githubUrl}
                      onGithubUrlChange={(v) => setDraft((d) => ({ ...d, githubUrl: v }))}
                      linkedinUrl={draft.linkedinUrl}
                      onLinkedinUrlChange={(v) => setDraft((d) => ({ ...d, linkedinUrl: v }))}
                      portfolioUrl={draft.portfolioUrl}
                      onPortfolioUrlChange={(v) =>
                        setDraft((d) => ({ ...d, portfolioUrl: v }))
                      }
                    />
                    <FormTextarea
                      id="pf-bio"
                      label="About you"
                      optional
                      value={draft.bio}
                      onChange={(e) => setDraft((d) => ({ ...d, bio: e.target.value }))}
                      rows={5}
                      placeholder="What you've built, what you're great at, what you're looking for."
                    />
                  </>
                )}

                {reviewStep === 2 && (
                  <>
                    <div>
                      <p className="mb-2 text-sm font-bold text-[color:var(--wf-ink)]">
                        Work experience
                      </p>
                      <ExperienceEditor
                        entries={draft.experience}
                        onChange={async (entries) =>
                          setDraft((d) => ({ ...d, experience: entries }))
                        }
                      />
                    </div>
                    <div>
                      <p className="mb-2 text-sm font-bold text-[color:var(--wf-ink)]">Education</p>
                      <EducationEditor
                        entries={draft.education}
                        onChange={async (entries) =>
                          setDraft((d) => ({ ...d, education: entries }))
                        }
                      />
                    </div>
                    <PreferencesFields
                      experienceLevel={draft.experienceLevel}
                      onExperienceLevelChange={(v) =>
                        setDraft((d) => ({ ...d, experienceLevel: v }))
                      }
                      workMode={draft.workMode}
                      onWorkModeChange={(v) => setDraft((d) => ({ ...d, workMode: v }))}
                      noticePeriod={draft.noticePeriod}
                      onNoticePeriodChange={(v) => setDraft((d) => ({ ...d, noticePeriod: v }))}
                      expectedSalary={draft.expectedSalary}
                      onExpectedSalaryChange={(v) =>
                        setDraft((d) => ({ ...d, expectedSalary: v }))
                      }
                      showExperienceLevel={false}
                    />
                    <label className="flex items-start gap-3 rounded-md border border-border bg-muted/40 p-3 text-sm">
                      <input
                        type="checkbox"
                        checked={discoveryConsent}
                        onChange={(event) => setDiscoveryConsent(event.target.checked)}
                        className="mt-1 size-4 accent-black"
                      />
                      <span>
                        Let verified companies discover this profile for relevant roles.
                        You can switch this off anytime in profile settings.
                      </span>
                    </label>
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                {reviewStep > 0 ? (
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    disabled={saving}
                    onClick={() => setReviewStep((s) => s - 1)}
                  >
                    Back
                  </button>
                ) : (
                  <button
                    type="button"
                    className="home-wf-btn home-wf-btn--outline"
                    disabled={saving}
                    onClick={() => setScreen("resume")}
                  >
                    Back
                  </button>
                )}
                <button
                  type="button"
                  className="home-wf-btn home-wf-btn--lime min-w-0 flex-1"
                  disabled={saving}
                  onClick={() => {
                    if (isLastStep) {
                      void saveAndContinue()
                    } else {
                      setReviewStep((s) => s + 1)
                    }
                  }}
                >
                  {saving ? "Saving…" : isLastStep ? "Save & continue" : "Continue"}
                </button>
                <button
                  type="button"
                  className="mkt-row-action"
                  disabled={saving}
                  onClick={() => void finishOnboarding()}
                >
                  Skip
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
